import type { CreateDiscountRequest } from '@mynt/backoffice-api-spec/discounts'
import {
  type AmountWithType,
  type CustomerDiscount,
  type DiscountItemId,
  type DiscountItemObject,
  type DiscountItemPrice,
  DiscountModifier,
  type Discounts,
  DiscountType,
} from './api/api'

export class DiscountManager {
  constructor(private data: { discount: CustomerDiscount; price: AmountWithType | undefined; name: string }) {}

  private getDiscountModifierName({ discount }: CustomerDiscount): DiscountModifier {
    switch (discount.modifier) {
      case DiscountModifier.ABSOLUTE: {
        return DiscountModifier.ABSOLUTE
      }

      case DiscountModifier.PERCENTAGE: {
        return DiscountModifier.PERCENTAGE
      }
    }

    throw new Error(`Unsupported discount type: ${discount.modifier}`)
  }

  private getPriceTypeName(price: AmountWithType): string {
    switch (price.type) {
      case 'coefficient': {
        return 'Per unit'
      }

      case 'constant': {
        return 'Flat fee'
      }
    }
  }

  private getDiscountItemPrice(price: AmountWithType | undefined): DiscountItemPrice | undefined {
    if (!price) return undefined

    return {
      type: this.getPriceTypeName(price),
      amount: {
        currency: price.currency,
        value: price.value,
      },
    }
  }

  getDiscountItemObject(): DiscountItemObject {
    return {
      id: this.data.discount.billableId as DiscountItemId,
      discount: {
        adornment: this.data.discount.discount.adornment,
        modifier: this.getDiscountModifierName(this.data.discount),
        expirationDate: this.data.discount.discount.expirationDate,
        value: this.data.discount.discount.value,
        type: this.data.discount.discount.type,
      },
      price: this.getDiscountItemPrice(this.data.price),
      name: this.data.name,
    }
  }
}

export class DiscountRequestManager {
  createDiscountRequest({ discount, formatDate }: { discount: Discounts; formatDate: (date: Date) => string }): CreateDiscountRequest {
    switch (discount.modifier) {
      case DiscountModifier.ABSOLUTE: {
        return {
          discountType: {
            type: DiscountType.FlatDiscount,
            modifierType: DiscountModifier.ABSOLUTE,
            absoluteAmount: {
              value: discount.value,
              currency: discount.adornment,
            },
          },
          expirationDate: discount.expirationDate ? formatDate(discount.expirationDate) : undefined,
        }
      }

      case DiscountModifier.PERCENTAGE:
        return {
          discountType: {
            type: DiscountType.FlatDiscount,
            modifierType: DiscountModifier.PERCENTAGE,
            percentageValue: discount.value,
          },
          expirationDate: discount.expirationDate ? formatDate(discount.expirationDate) : undefined,
        }
    }

    throw new Error(`Unsupported discount type: ${discount.type}`)
  }
}
